import React, { useContext, useEffect, useState } from "react";
import CreateAccount from "./CreateAccount";
import CreateProfile from "./CreateProfile";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { device } from "../../lib/breakpoints";
import styled from "styled-components";
import { APP_LOGO, APP_NAME } from "../../helpers/constants";
import { Highlight, Spacer } from "../../styles/CommonElements";
import Button from "../Button";
import ThirdPartyAuth from "./ThirdPartyAuth";
import { APP_TERMS_ROUTE } from "../../helpers/routeNames";
import { AuthContext } from "../../context/AuthProvider";
import AccountCreationForm from "./AccountCreationForm";
import { useMutation } from "@tanstack/react-query";
import { registerFirebaseAuth } from "../../helpers/authentication";
import { message } from "antd";

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const [onWelcomeScreen, setOnWelcomeScreen] = useState(true);
  const [accountCreated, setAccountCreated] = useState(false);
  const navigate = useNavigate();

  const { user, userInfo, isUserInfoLoading, isThirdPartyAuthUser } =
    useContext(AuthContext);

  const {
    mutate: createAccount,
    isPending: accountCreationPending,
    error: createAccountErrors,
  } = useMutation({
    mutationFn: async ({ email, password }) => {
      const result = await registerFirebaseAuth({
        email,
        password,
      });

      if (!result) {
        throw new Error("An account with this email already exists");
      }
    },
    onSuccess: () => {
      setAccountCreated(true);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  // Redirect if user is already logged in and has a profile
  useEffect(() => {
    if (user && !isUserInfoLoading) {
      if (userInfo) {
        navigate("/use");
      } else if (!isThirdPartyAuthUser) {
        setAccountCreated(true);
      }
    }
  }, [user, userInfo, isUserInfoLoading, isThirdPartyAuthUser, navigate]);

  const onAccountCreationSubmit = (data) => {
    if (isThirdPartyAuthUser) {
      setAccountCreated(true);
      return;
    }

    createAccount(data);
  };

  return (
    <SignupContainer>
      {!accountCreated && onWelcomeScreen && (
        <WelcomeContainer>
          <LogoWrapper onClick={() => navigate("/")}>
            <img src={APP_LOGO} alt="" />
          </LogoWrapper>
          <Spacer $marginTop="0.5rem" />
          <Title>Sign Up for {APP_NAME}</Title>
          <Description>It's time to see the new you</Description>
          <Spacer $marginTop="1.5rem" />
          <Button
            size="large"
            onClick={() => setOnWelcomeScreen(false)}
            style={{
              background: "var(--gradient-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              boxShadow: "0 4px 18px rgba(44, 95, 45, 0.15)",
              fontFamily: "var(--font-secondary)",
              fontWeight: "700",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "var(--gradient-primary-dark)";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 32px rgba(44, 95, 45, 0.25)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "var(--gradient-primary)";
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 18px rgba(44, 95, 45, 0.15)";
            }}
          >
            Sign Up with Email
          </Button>

          {/*
          <Spacer $marginTop="1rem" />
          <ThirdPartyAuth
            cta="Sign up"
            onCompletion={() => {
              setOnWelcomeScreen(false);
            }}
          />
          */}

          <Spacer $marginTop="16px" />
          <Terms>
            By continuing, you agree to our{" "}
            <Link
              to={APP_TERMS_ROUTE}
              target="_blank"
              style={{ color: "black", fontWeight: 600 }}
            >
              Terms of Service
            </Link>
            .
          </Terms>
          <Spacer $marginTop="16px" />
          <LoginCta>
            <Highlight onClick={() => navigate("/login")}>
              Already have an account? Log In
            </Highlight>
          </LoginCta>
        </WelcomeContainer>
      )}

      {!accountCreated && !onWelcomeScreen && (
        <WelcomeContainer>
          <LogoWrapper onClick={() => navigate("/")}>
            <img src={APP_LOGO} alt="" />
          </LogoWrapper>
          <Spacer $marginTop="0.5rem" />
          <Title>Sign Up for {APP_NAME}</Title>
          {!isThirdPartyAuthUser && (
            <Description>Create your {APP_NAME} account</Description>
          )}
          <Spacer $marginTop="1rem" />
          <FormContainer>
            <AccountCreationForm
              isThirdPartyUser={isThirdPartyAuthUser}
              onSubmit={onAccountCreationSubmit}
              isSubmitting={accountCreationPending}
            />

            {createAccountErrors && (
              <MutationError>{createAccountErrors.message}</MutationError>
            )}
          </FormContainer>
          <LoginCta>
            <Highlight onClick={() => navigate("/login")}>
              Already have an account? Log In
            </Highlight>
          </LoginCta>
        </WelcomeContainer>
      )}

      {accountCreated && <CreateProfile />}
    </SignupContainer>
  );
}

export const SignupContainer = styled.div`
  @media ${device.tablet} {
    width: 500px;
    margin-top: 5vh;
    margin-inline: auto;
  }

  @media ${device.desktop} {
    width: 600px;
    margin-top: 10vh;
    margin-inline: auto;
  }
`;

const WelcomeContainer = styled.div`
  margin: 1.5rem auto;
  width: 90%;
  text-align: center;
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.5rem;
  height: 60px;
  width: auto;
  margin: auto;
`;

const Title = styled.h1`
  font-family: var(--font-primary);
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: var(--color-text);
`;

const Description = styled.p`
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  font-size: 16px;
  font-weight: 400;
  margin: 8px 0 0 0;
`;

const LoginCta = styled.div`
  color: var(--color-text);
  text-align: center;
  font-family: var(--font-secondary);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%;
  margin-top: 16px;

  span {
    cursor: pointer;
  }
`;

const MutationError = styled.div`
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  color: var(--color-error);
  margin-top: 12px;
`;

const Terms = styled.div`
  color: var(--color-text-light);
  text-align: center;
  font-size: 0.625rem;
  font-weight: 400;
`;

const FormContainer = styled.div`
  text-align: left;
`;
